import { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { TrendingUp, TrendingDown, Wallet, Receipt, Lightbulb, Loader2, ArrowLeftRight, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = ({ token }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [detectingTransfers, setDetectingTransfers] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    if (stats && stats.categories_breakdown?.length > 0) {
      fetchInsights();
    }
  }, [stats]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/stats/overview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInsights = async () => {
    setInsightsLoading(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/stats/insights`,
        { categories: stats.categories_breakdown },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInsights(response.data.insights);
    } catch (error) {
      console.error('Error fetching insights:', error);
    } finally {
      setInsightsLoading(false);
    }
  };

  const detectTransfers = async () => {
    setDetectingTransfers(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/transactions/detect-transfers`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      if (response.data.transfers_detected > 0) {
        fetchStats(); // Refresh stats
      }
    } catch (error) {
      toast.error('Failed to detect transfers');
    } finally {
      setDetectingTransfers(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-state">
        <p className="font-body text-muted-foreground">Loading...</p>
      </div>
    );
  }

  const chartData = [
    { name: 'Income', value: stats?.total_income || 0, fill: '#10B981' },
    { name: 'Expense', value: stats?.total_expense || 0, fill: '#EF4444' },
  ];

  const categoryData = stats?.categories_breakdown || [];
  const expenseCategories = categoryData.filter(cat => cat.type === 'expense').slice(0, 6);
  const incomeCategories = categoryData.filter(cat => cat.type === 'income').slice(0, 6);

  const EXPENSE_COLORS = ['#EF4444', '#F59E0B', '#EC4899', '#8B5CF6', '#3B82F6', '#06B6D4'];
  const INCOME_COLORS = ['#10B981', '#22C55E', '#84CC16', '#14B8A6', '#0EA5E9', '#6366F1'];

  const renderCustomLabel = ({ name, percent }) => {
    if (percent < 0.05) return null;
    return `${(percent * 100).toFixed(0)}%`;
  };

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="text-4xl md:text-5xl font-heading font-semibold tracking-tight text-foreground">
          Dashboard
        </h1>
        <p className="mt-1 text-base font-body text-muted-foreground">
          Overview of your financial health
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        <div className="md:col-span-3 bg-card border border-border rounded-sm shadow-sm p-6" data-testid="total-balance-card">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-10 w-10 rounded-sm bg-primary/10 flex items-center justify-center">
              <Wallet className="h-5 w-5 text-primary" />
            </div>
            <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider">
              Total Balance
            </p>
          </div>
          <p className="text-4xl font-heading font-bold tracking-tighter text-foreground">
            ${(stats?.balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        </div>

        <div className="md:col-span-3 bg-card border border-border rounded-sm shadow-sm p-6" data-testid="total-income-card">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-10 w-10 rounded-sm bg-green-50 flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-green-600" />
            </div>
            <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider">
              True Income
            </p>
          </div>
          <p className="text-4xl font-heading font-bold tracking-tighter text-green-600">
            ${(stats?.total_income || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
          <p className="text-xs text-muted-foreground mt-1">Excludes internal transfers</p>
        </div>

        <div className="md:col-span-3 bg-card border border-border rounded-sm shadow-sm p-6" data-testid="total-expense-card">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-10 w-10 rounded-sm bg-red-50 flex items-center justify-center">
              <TrendingDown className="h-5 w-5 text-red-600" />
            </div>
            <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider">
              True Expenses
            </p>
          </div>
          <p className="text-4xl font-heading font-bold tracking-tighter text-red-600">
            ${(stats?.total_expense || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
          <p className="text-xs text-muted-foreground mt-1">Excludes internal transfers</p>
        </div>

        <div className="md:col-span-3 bg-card border border-border rounded-sm shadow-sm p-6" data-testid="internal-transfers-card">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-10 w-10 rounded-sm bg-blue-50 flex items-center justify-center">
              <ArrowLeftRight className="h-5 w-5 text-blue-600" />
            </div>
            <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider">
              Internal Transfers
            </p>
          </div>
          <p className="text-4xl font-heading font-bold tracking-tighter text-blue-600">
            ${(stats?.total_transfers || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
          <p className="text-xs text-muted-foreground mt-1">{stats?.transfer_count || 0} transfers detected</p>
          <Button 
            variant="outline" 
            size="sm" 
            className="mt-2 text-xs"
            onClick={detectTransfers}
            disabled={detectingTransfers}
            data-testid="detect-transfers-btn"
          >
            {detectingTransfers ? (
              <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Scanning...</>
            ) : (
              <><RefreshCw className="h-3 w-3 mr-1" /> Scan for Transfers</>
            )}
          </Button>
        </div>

        <div className="md:col-span-8 bg-card border border-border rounded-sm shadow-sm p-6" data-testid="income-expense-chart">
          <h2 className="text-2xl font-heading font-medium mb-4">Income vs Expenses</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
              <XAxis dataKey="name" stroke="#78716C" style={{ fontFamily: 'JetBrains Mono' }} />
              <YAxis stroke="#78716C" style={{ fontFamily: 'JetBrains Mono' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #E7E5E4',
                  borderRadius: '0.125rem',
                  fontFamily: 'JetBrains Mono',
                }}
                formatter={(value) => ['$' + value.toLocaleString('en-US', { minimumFractionDigits: 2 }), '']}
              />
              <Bar dataKey="value" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* AI Insights Panel */}
        <div className="md:col-span-4 bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-200 rounded-sm shadow-sm p-6" data-testid="ai-insights">
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb className="h-5 w-5 text-amber-500" />
            <h2 className="text-xl font-heading font-medium">AI Insights</h2>
          </div>
          {insightsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
              <span className="ml-2 text-sm text-muted-foreground">Analyzing your spending...</span>
            </div>
          ) : insights ? (
            <div className="space-y-3 text-sm font-body text-gray-700">
              {insights.split('\n').filter(line => line.trim()).map((line, i) => (
                <p key={i} className="leading-relaxed">{line}</p>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Add transactions to get personalized insights about your spending habits.</p>
          )}
        </div>

        {/* Expense Breakdown Pie Chart */}
        <div className="md:col-span-6 bg-card border border-border rounded-sm shadow-sm p-6" data-testid="expense-breakdown-chart">
          <h2 className="text-2xl font-heading font-medium mb-2">Expense Breakdown</h2>
          <p className="text-sm text-muted-foreground mb-4">Where your money goes</p>
          {expenseCategories.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={expenseCategories}
                  dataKey="amount"
                  nameKey="category_name"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  innerRadius={40}
                  label={renderCustomLabel}
                  labelLine={false}
                >
                  {expenseCategories.map((entry, index) => (
                    <Cell key={`expense-cell-${index}`} fill={EXPENSE_COLORS[index % EXPENSE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #E7E5E4',
                    borderRadius: '0.125rem',
                    fontFamily: 'JetBrains Mono',
                  }}
                  formatter={(value) => ['$' + value.toLocaleString('en-US', { minimumFractionDigits: 2 }), '']}
                />
                <Legend 
                  layout="vertical" 
                  align="right" 
                  verticalAlign="middle"
                  wrapperStyle={{ fontSize: '12px', fontFamily: 'Inter' }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-muted-foreground font-body mt-12">No expense data</p>
          )}
        </div>

        {/* Income Breakdown Pie Chart */}
        <div className="md:col-span-6 bg-card border border-border rounded-sm shadow-sm p-6" data-testid="income-breakdown-chart">
          <h2 className="text-2xl font-heading font-medium mb-2">Income Breakdown</h2>
          <p className="text-sm text-muted-foreground mb-4">Where your money comes from</p>
          {incomeCategories.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={incomeCategories}
                  dataKey="amount"
                  nameKey="category_name"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  innerRadius={40}
                  label={renderCustomLabel}
                  labelLine={false}
                >
                  {incomeCategories.map((entry, index) => (
                    <Cell key={`income-cell-${index}`} fill={INCOME_COLORS[index % INCOME_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #E7E5E4',
                    borderRadius: '0.125rem',
                    fontFamily: 'JetBrains Mono',
                  }}
                  formatter={(value) => ['$' + value.toLocaleString('en-US', { minimumFractionDigits: 2 }), '']}
                />
                <Legend 
                  layout="vertical" 
                  align="right" 
                  verticalAlign="middle"
                  wrapperStyle={{ fontSize: '12px', fontFamily: 'Inter' }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-muted-foreground font-body mt-12">No income data</p>
          )}
        </div>

        <div className="md:col-span-12 bg-card border border-border rounded-sm shadow-sm p-6" data-testid="transaction-summary">
          <div className="flex items-center gap-3 mb-4">
            <Receipt className="h-6 w-6 text-primary" />
            <h2 className="text-2xl font-heading font-medium">Transaction Summary</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="border border-border rounded-sm p-4">
              <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-1">
                Total Transactions
              </p>
              <p className="text-xl font-mono font-medium text-foreground">
                {stats?.transaction_count || 0}
              </p>
            </div>
            <div className="border border-border rounded-sm p-4">
              <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-1">
                Categories Tracked
              </p>
              <p className="text-xl font-mono font-medium text-foreground">
                {categoryData.length}
              </p>
            </div>
            <div className="border border-border rounded-sm p-4">
              <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-1">
                Average Transaction
              </p>
              <p className="text-xl font-mono font-medium text-foreground">
                ${stats?.transaction_count > 0 ? ((stats.total_income + stats.total_expense) / stats.transaction_count).toFixed(2) : '0.00'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
